import {
  ArticleAnalysisHistory,
  CannibalizationAnalysis,
  ContentGapAnalysis,
  ExtGapComparison,
  GapTimelineResult,
  GeneratedArticle,
  GeneratedOutline,
  KeywordAnalysis,
  KeywordAnalysisResult,
  LinkStructureAnalysis
} from '@/common/model/media/articleCreator'

export const ArticleApiPath = {
  ANALYZE_KEYWORDS: '/api/sites/{site_id}/article/keyword-analysis', // POST Analyze Keywords
  ANALYZE_KEYWORDS_STREAM: '/api/sites/{site_id}/article/keyword-analysis/stream', // POST Analyze Keywords Stream
  GENERATE_ARTICLE: '/api/sites/{site_id}/article/generate', // POST Generate Article
  GENERATE_OUTLINE: '/api/sites/{site_id}/article/outline', // POST Generate Article Outline
  ANALYZE_CONTENT_GAP: '/api/sites/{site_id}/article/content-gap', // GET Analyze Content Gap（同期実行）
  RUN_CONTENT_GAP: '/api/sites/{site_id}/article/content-gap/run', // POST Run Content Gap（バックグラウンド実行）
  GET_ANALYSIS_HISTORY: '/api/sites/{site_id}/article/history', // GET Get Analysis History
  GET_ANALYSIS_DETAIL: '/api/sites/{site_id}/article/history/{history_id}', // GET Get Analysis Detail
  COMPARE_CONTENT_GAP: '/api/sites/{site_id}/article/content-gap/compare', // GET Compare Content Gap
  DETECT_CANNIBALIZATION: '/api/sites/{site_id}/article/content-gap/cannibalization', // GET Detect Cannibalization（同期実行）
  RUN_CANNIBALIZATION: '/api/sites/{site_id}/article/content-gap/cannibalization/run', // POST Run Cannibalization（バックグラウンド実行）
  COMPARE_CANNIBALIZATION: '/api/sites/{site_id}/article/content-gap/cannibalization/compare', // GET Compare Cannibalization
  ANALYZE_LINK_STRUCTURE: '/api/sites/{site_id}/article/content-gap/link-structure', // GET Analyze Link Structure（同期実行）
  RUN_LINK_STRUCTURE: '/api/sites/{site_id}/article/content-gap/link-structure/run', // POST Run Link Structure（バックグラウンド実行）
  COMPARE_LINK_STRUCTURE: '/api/sites/{site_id}/article/content-gap/link-structure/compare', // GET Compare Link Structure
  TIMELINE_CANNIBALIZATION: '/api/sites/{site_id}/article/content-gap/cannibalization/timeline', // GET Timeline Cannibalization
  TIMELINE_LINK_STRUCTURE: '/api/sites/{site_id}/article/content-gap/link-structure/timeline' // GET Timeline Link Structure
}

export type ArticleApiPath = (typeof ArticleApiPath)[keyof typeof ArticleApiPath]

export interface AnalyzeKeywordsVars {
  site_id: number
  keywords: string[]
  site_url?: string | null
  mode?: string
}

export interface AnalyzeKeywordsStreamVars {
  site_id: number
  keywords: string[]
  site_url?: string | null
  mode?: string
}

export interface GenerateArticleVars {
  site_id: number
  keyword: string
  title?: string
  outline?: string[] | null
  tone?: string
  persona?: string
  target_length?: number
  title_candidates?: string[]
  internal_link_candidates?: { title: string; slug: string }[]
}

export interface GenerateOutlineVars {
  site_id: number
  keyword: string
}

export interface AnalyzeContentGapVars {
  site_id: number
}

export interface GetAnalysisHistoryVars {
  site_id: number
  analysis_type?: string
  page?: number
  per_page?: number
}

export interface GetAnalysisDetailVars {
  site_id: number
  history_id: number
}

export interface CompareContentGapVars {
  site_id: number
}

export interface DetectCannibalizationVars {
  site_id: number
}

export interface CompareCannibalizationVars {
  site_id: number
}

export interface AnalyzeLinkStructureVars {
  site_id: number
}

export interface CompareLinkStructureVars {
  site_id: number
}

export interface TimelineCannibalizationVars {
  site_id: number
}

export interface TimelineLinkStructureVars {
  site_id: number
}

export interface AnalyzeKeywordsResponse {
  site_id: number
  input_keywords: string[]
  mode: string
  analysis: KeywordAnalysis
}

// SSEストリーミング（keyword-analysis/stream）のイベント
export interface KeywordAnalysisProgressEvent {
  step: number
  total_steps: number
  percent: number
  message: string
  detail?: string
}

export type AnalyzeKeywordsStreamResult = KeywordAnalysisResult

export interface GenerateArticleResponse {
  site_id: number
  keyword: string
  article: GeneratedArticle
}

export interface GenerateOutlineResponse {
  site_id: number
  keyword: string
  outline: GeneratedOutline
}

export interface AnalyzeContentGapResponse {
  site_id: number
  analysis: ContentGapAnalysis
  id: number | null
}

export interface GetAnalysisHistoryResponse {
  total: number
  histories: ArticleAnalysisHistory[]
}

export interface GetAnalysisDetailResponse {
  id: number
  analysis_type: string
  input_data: Record<string, unknown>
  result_data: Record<string, unknown>
  summary: string | null
  created_at: string | null
}

export interface CompareContentGapResponse {
  available: boolean
  message?: string
  current_date: string | null
  previous_date: string | null
  new_gaps: string[]
  resolved_gaps: string[]
  persistent_gaps: string[]
  current_gap_count: number
  previous_gap_count: number
}

export interface DetectCannibalizationResponse {
  site_id: number
  analysis: CannibalizationAnalysis
  id: number | null
}

export interface AnalyzeLinkStructureResponse {
  site_id: number
  analysis: LinkStructureAnalysis
  id: number | null
}

// バックグラウンド実行（run）の入力は同期実行と同じくサイトIDのみ
export interface RunAnalysisVars {
  site_id: number
}

// バックグラウンド実行（run）の起動レスポンス。作成直後のレコードidと状態を返す
export interface RunAnalysisResponse {
  id: number
  analysis_type: string
  status: string // analyzing
}

export type CompareExtGapResponse = ExtGapComparison

export type TimelineExtGapResponse = GapTimelineResult
